import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, Select, Space, Typography, Row, Col, 
  Empty 
} from 'antd';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer 
} from 'recharts';
import { 
  LineChartOutlined, AreaChartOutlined, BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const { Title, Text } = Typography;
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(timezone);

const HourlyDataChart = ({ data, loading }) => {
  const [chartType, setChartType] = useState('line');
  const [selectedMetrics, setSelectedMetrics] = useState([]);

  // Mevcut metriklerin listesini al
  const availableMetrics = useMemo(() => {
    if (!data || data.length === 0) return [];
    const metrics = new Set();
    
    // Temel metrikler
    metrics.add('revenue');
    metrics.add('orders'); 
    metrics.add('productSales');
    metrics.add('totalCost');
    
    // Maliyet detayları
    data.forEach(item => {
      if (item.data && typeof item.data === 'object') {
        Object.keys(item.data).forEach(key => {
          if (key.includes('Cost')) {
            metrics.add(key);
          }
        });
      }
    });
    
    return Array.from(metrics);
  }, [data]);

  // Veriyi grafik formatına dönüştür
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Veriyi tarihe göre sırala ve formatla
    return data
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map(item => {
        const itemData = item.data || {};
        
        const formattedItem = {
          date: item.date,
          dateLabel: dayjs.utc(item.date).tz('Europe/Istanbul').format('DD.MM HH:mm'),
          hour: dayjs.utc(item.date).tz('Europe/Istanbul').hour(),
          day: dayjs.utc(item.date).tz('Europe/Istanbul').format('DD.MM'),
          // Ana metrikler
          revenue: Number(itemData.revenue) || 0,
          orders: Number(itemData.orders) || 0,
          productSales: Number(itemData.productSales) || 0,
          // Maliyet metrikleri
          metaCost: Number(itemData.metaCost) || 0,
          googleCost: Number(itemData.googleCost) || 0,
          metaBoostCost: Number(itemData.metaBoostCost) || 0,
          // Toplam maliyet
          totalCost: (Number(itemData.metaCost) || 0) + 
                    (Number(itemData.googleCost) || 0) + 
                    (Number(itemData.metaBoostCost) || 0)
        };

        // Diğer metrikler için
        availableMetrics.forEach(metric => {
          if (!formattedItem.hasOwnProperty(metric)) {
            const value = itemData[metric];
            formattedItem[metric] = (value !== undefined && value !== null) ? 
              (isNaN(Number(value)) ? 0 : Number(value)) : 0;
          }
        });

        return formattedItem;
      });
  }, [data, availableMetrics]);

  // İlk yüklemede revenue ve totalCost seç
  useEffect(() => {
    if (selectedMetrics.length === 0 && chartData.length > 0) {
      setSelectedMetrics(['revenue', 'totalCost']);
    }
  }, [chartData.length, selectedMetrics.length]);


  // Renk paleti
  const colors = [
    '#1890ff', '#52c41a', '#fa8c16', '#eb2f96', 
    '#722ed1', '#13c2c2', '#fa541c', '#a0d911'
  ];

  // Grafik bileşenini seç
  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    const xAxisProps = {
      dataKey: 'dateLabel',
      tick: { fontSize: 12 },
      angle: -45,
      textAnchor: 'end',
      height: 60
    };

    const yAxisProps = {
      tick: { fontSize: 12 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <RechartsTooltip 
              labelFormatter={(label) => `Saat: ${label}`}
              formatter={(value, name) => [
                typeof value === 'number' ? value.toLocaleString() : value, 
                name
              ]}
            />
            <Legend />
            {selectedMetrics.map((metric, index) => (
              <Area
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <RechartsTooltip 
              labelFormatter={(label) => `Saat: ${label}`}
              formatter={(value, name) => [
                typeof value === 'number' ? value.toLocaleString() : value, 
                name
              ]}
            />
            <Legend />
            {selectedMetrics.map((metric, index) => (
              <Bar
                key={metric}
                dataKey={metric}
                fill={colors[index % colors.length]}
              />
            ))}
          </BarChart>
        );

      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            
            {/* Sol Y ekseni - Revenue */}
            <YAxis 
              yAxisId="revenue"
              {...yAxisProps} 
              domain={['auto', 'auto']}
              label={{ 
                value: 'Gelir (₺)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#52c41a' }
              }}
            />
            
            {/* Sağ Y ekseni - Costs */}
            <YAxis 
              yAxisId="cost"
              orientation="right"
              {...yAxisProps}
              domain={['auto', 'auto']}
              label={{ 
                value: 'Maliyet (₺)', 
                angle: 90, 
                position: 'insideRight',
                style: { textAnchor: 'middle', fill: '#fa8c16' }
              }}
            />
            
            <RechartsTooltip 
              labelFormatter={(label) => `Saat: ${label}`}
              formatter={(value, name) => [
                typeof value === 'number' ? value.toLocaleString('tr-TR') + ' ₺' : value, 
                name === 'revenue' ? 'Gelir' : 
                name === 'totalCost' ? 'Toplam Maliyet' :
                name === 'orders' ? 'Sipariş' : 
                name === 'productSales' ? 'Ürün Satışı' : name
              ]}
            />
            <Legend />
            
            {selectedMetrics.map((metric, index) => {
              const isRevenue = metric === 'revenue';
              const isCost = metric.includes('Cost') || metric === 'totalCost';
              const yAxisId = isRevenue ? 'revenue' : isCost ? 'cost' : 'revenue';
              
              return (
                <Line
                  key={metric}
                  yAxisId={yAxisId}
                  type="monotone"
                  dataKey={metric}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name={metric === 'revenue' ? 'Gelir' : 
                        metric === 'totalCost' ? 'Toplam Maliyet' :
                        metric === 'orders' ? 'Sipariş' : 
                        metric === 'productSales' ? 'Ürün Satışı' : metric}
                />
              );
            })}
          </LineChart>
        );
    }
  };

  if (!data || data.length === 0) {
    return (
      <Empty 
        description="Görüntülenecek saatlik veri yok"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {/* Kontrol Paneli */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>Grafik Tipi</Text>
            <Select
              value={chartType}
              onChange={setChartType}
              style={{ width: '100%' }}
            >
              <Option value="line">
                <LineChartOutlined /> Çizgi Grafik
              </Option>
              <Option value="area">
                <AreaChartOutlined /> Alan Grafik
              </Option>
              <Option value="bar">
                <BarChartOutlined /> Çubuk Grafik
              </Option>
            </Select>
          </Space>
        </Col>

        <Col xs={24} sm={8}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>Gösterilecek Metrikler</Text>
            <Select
              mode="multiple"
              value={selectedMetrics}
              onChange={setSelectedMetrics}
              style={{ width: '100%' }}
              placeholder="Metrik seçin"
              maxTagCount={2}
            >
              {availableMetrics.map(metric => (
                <Option key={metric} value={metric}>
                  {metric}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>

        <Col xs={24} sm={8}>
          {/* Boş alan - grafik bilgisi kaldırıldı */}
        </Col>
      </Row>


      {/* Grafik */}
      <Card>
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default HourlyDataChart;